import { Box, Button, Card, CardContent, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ListPageLayout from '../../../components/ListPageLayout';
import SelectClub from '../../../components/SelectClub';
import { useFetch } from '../../../hooks/useFecth';

const STATUS_META = {
    PENDING: { label: 'Pendente', color: 'warning' },
    RECEIVED: { label: 'Recebida', color: 'success' },
    CONFIRMED: { label: 'Confirmada', color: 'success' },
    OVERDUE: { label: 'Atrasada', color: 'error' },
    REFUNDED: { label: 'Estornada', color: 'default' },
    DELETED: { label: 'Removida', color: 'default' },
    CANCELED: { label: 'Cancelada', color: 'default' },
    EXPIRED: { label: 'Expirada', color: 'default' },
};

function formatDate(value) {
    if (!value) return '—';
    try {
        const d = typeof value === 'string' ? parseISO(value) : new Date(value);
        return isValid(d) ? format(d, 'dd/MM/yyyy') : '—';
    } catch {
        return '—';
    }
}

function money(value) {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function statusMeta(status) {
    return STATUS_META[status] || { label: status || '—', color: 'default' };
}

function paymentLink(invoice) {
    return invoice?.invoiceUrl || invoice?.bankSlipUrl || invoice?.receiptPayment || null;
}

export default function AdminSubscriptions() {
    const [clubId, setClubId] = useState(null);
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(1);

    const filters = useMemo(
        () => ({
            ...(clubId ? { clubId } : {}),
            ...(status ? { status } : {}),
            page,
            limit: 20,
        }),
        [clubId, status, page],
    );

    const { data, isLoading } = useFetch('invoices/subscriptions', { params: filters });
    const rows = data?.data ?? [];
    const counts = data?.counts || {};

    return (
        <ListPageLayout
            title="Assinaturas"
            subtitle="Invoices emitidas das assinaturas dos clubes."
            counts={[
                { label: 'invoices', value: counts.total ?? data?.total ?? rows.length, color: 'primary' },
                {
                    label: 'atrasadas',
                    value: counts.overdue ?? 0,
                    color: 'error',
                    selected: status === 'OVERDUE',
                    onClick: () => {
                        setStatus(current => (current === 'OVERDUE' ? '' : 'OVERDUE'));
                        setPage(1);
                    },
                },
                { label: 'pendentes', value: counts.pending ?? 0, color: 'warning' },
                { label: 'pagas', value: counts.paid ?? 0, color: 'success' },
            ]}
            loading={isLoading && !data}
            filterActive={Boolean(clubId || status)}
            onClearFilters={() => {
                setClubId(null);
                setStatus('');
                setPage(1);
            }}
            filterPanel={() => (
                <Stack spacing={2}>
                    <SelectClub value={clubId} onChange={e => { setClubId(e.target.value); setPage(1); }} />
                    <TextField
                        select
                        label="Status"
                        value={status}
                        onChange={e => {
                            setStatus(e.target.value);
                            setPage(1);
                        }}
                        fullWidth
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {Object.entries(STATUS_META).map(([value, meta]) => (
                            <MenuItem key={value} value={value}>
                                {meta.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            )}
            pagination={{
                page: data?.page ?? page,
                pages: data?.totalPages || 1,
                total: data?.total ?? rows.length,
                pageSize: 20,
                onChange: (_, next) => setPage(next),
                itemLabel: 'invoices',
            }}
        >
            {() => (
                <Stack spacing={1.5} sx={{ p: 2 }}>
                    {rows.map(invoice => {
                        const meta = statusMeta(invoice.status);
                        const link = paymentLink(invoice);
                        return (
                            <Card key={invoice.id} variant="outlined">
                                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                                            <Typography fontWeight={700} noWrap>
                                                {invoice.club?.name || '—'}
                                            </Typography>
                                            <Chip size="small" color={meta.color} label={meta.label} />
                                        </Stack>
                                        <Typography variant="body2">
                                            {money(invoice.value)} · venc. {formatDate(invoice.dueDate)}
                                            {invoice.plan?.name ? ` · ${invoice.plan.name}` : ''}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            #{invoice.id}
                                            {invoice.chargeId ? ` · ${invoice.chargeId}` : ''}
                                            {invoice.description ? ` · ${invoice.description}` : ''}
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            {link && (
                                                <Button size="small" component="a" href={link} target="_blank" rel="noreferrer">
                                                    {['PENDING', 'OVERDUE'].includes(invoice.status) ? 'Abrir boleto' : 'Comprovante'}
                                                </Button>
                                            )}
                                            {invoice.club?.id && (
                                                <Button size="small" component={Link} to={`/dashboard/clubes/${invoice.club.id}`}>
                                                    Clube
                                                </Button>
                                            )}
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        );
                    })}
                    {!isLoading && rows.length === 0 && (
                        <Box sx={{ py: 4 }}>
                            <Typography color="text.secondary" textAlign="center">
                                Nenhuma invoice de assinatura encontrada.
                            </Typography>
                        </Box>
                    )}
                </Stack>
            )}
        </ListPageLayout>
    );
}
